// Docked on-screen numeric keypad for fast, glove-friendly score entry on
// phones. Rendered only on mobile (the parent gates it) and only while a
// Score/X field is active. Buttons never steal focus from the active input
// (tabIndex=-1 + preventDefault on mousedown) so "Next" can advance from it.
//
// Layout is a calculator pad (7-8-9 on top): large keys sized for a gloved
// thumb, a live readout of the value as it is typed, a quick-fill key for the
// clean maximum (100 / 10), and a full-width Next as the primary action.

const Key = ({ children, onPress, className = "", ariaLabel }) => (
  <button
    type="button"
    tabIndex={-1}
    aria-label={ariaLabel}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onPress}
    className={`flex h-16 select-none items-center justify-center rounded-xl text-2xl font-semibold
      ring-1 ring-inset transition-transform duration-75 active:scale-[0.96] ${className}`}
  >
    {children}
  </button>
);

const DIGIT_KEYS = "bg-white text-ink-900 ring-ink-200 hover:bg-ink-50";

const NumericKeypad = ({
  context,
  fieldName,
  value,
  max,
  quickLabel,
  onDigit,
  onQuick,
  onBackspace,
  onClear,
  onNext,
  onDone,
  onSwitchMode,
}) => {
  const hasValue = value !== null && value !== undefined && value !== "";
  const display = hasValue ? String(value) : "–";

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-brand-200 bg-white/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-pop backdrop-blur sm:hidden"
      role="group"
      aria-label="Numeric keypad"
    >
      {/* Readout: which cell is being edited, and the value so far. */}
      <div className="mb-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          {context && (
            <div className="truncate text-xs font-medium text-ink-500">
              {context}
            </div>
          )}
          <div className="text-sm font-semibold text-ink-700">{fieldName}</div>
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className={`tabular-nums text-3xl font-bold leading-none ${
              hasValue ? "text-brand-700" : "text-ink-300"
            }`}
          >
            {display}
          </span>
          {max ? (
            <span className="text-sm font-medium text-ink-400">/ {max}</span>
          ) : null}
        </div>
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDone}
          className="btn-ghost btn-sm shrink-0"
        >
          Done
        </button>
      </div>

      {/* Calculator layout: 7-8-9 / 4-5-6 / 1-2-3 / quick-0-backspace. */}
      <div className="grid grid-cols-3 gap-2">
        {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((d) => (
          <Key
            key={d}
            ariaLabel={`Digit ${d}`}
            onPress={() => onDigit(String(d))}
            className={DIGIT_KEYS}
          >
            {d}
          </Key>
        ))}
        <Key
          ariaLabel={`Fill ${quickLabel}`}
          onPress={onQuick}
          className="bg-brand-50 text-brand-700 ring-brand-200 hover:bg-brand-100"
        >
          {quickLabel}
        </Key>
        <Key ariaLabel="Digit 0" onPress={() => onDigit("0")} className={DIGIT_KEYS}>
          0
        </Key>
        <Key
          ariaLabel="Backspace"
          onPress={onBackspace}
          className="bg-white text-ink-500 ring-ink-200 hover:bg-ink-50"
        >
          ⌫
        </Key>
      </div>

      {/* Clear is secondary; Next carries the flow and gets the width + brand. */}
      <div className="mt-2 flex items-center gap-2">
        {onSwitchMode && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSwitchMode}
            className="btn-ghost btn-sm shrink-0"
            aria-label="Switch to shot-by-shot entry"
          >
            Tap
          </button>
        )}
        <Key
          ariaLabel="Clear field"
          onPress={onClear}
          className="h-12 flex-1 text-base bg-white text-ink-600 ring-ink-200 hover:bg-ink-50"
        >
          Clear
        </Key>
        <Key
          ariaLabel="Next field"
          onPress={onNext}
          className="h-12 flex-[2] text-base bg-brand-600 text-white ring-brand-600 hover:bg-brand-700"
        >
          Next ›
        </Key>
      </div>
    </div>
  );
};

export default NumericKeypad;
